import React from 'react'
import PropTypes from 'prop-types'
import {FormattedMessage, FormattedNumber, useIntl, defineMessages} from 'react-intl'
import {
    Box,
    Flex,
    Text,
    Image,
    Stack,
    Divider
} from '@salesforce/retail-react-app/app/components/shared/ui'
import {GarageBrandIcon} from '../../../components/icons'
import {BRAND_NAME, ORDER_PRODUCTS_RENDER_MODES, ORDER_RETURN_RAW_STATUS} from '../constant'
import {ShippingIcon, InStoreShipping} from '../../../components/icons'
import { SHIPPING_METHODS } from '../constant'
import {useMultiStyleConfig, useBreakpointValue} from '@chakra-ui/react'
import useMultiSite from '@salesforce/retail-react-app/app/hooks/use-multi-site'

const E_GIFT_CARD_SHIPPING_ID = 'EGiftCard'
const STANDARD_ECOM_SHIPPING_ID = 'GB Standard Ecom'
const ProductCard = ({
    productImageURL,
    orderBrand,
    productName,
    color,
    size,
    length,
    quantity,
    regularPrice,
    salePrice,
    currency,
    styleNumber
}) => {
    const isOnSale = salePrice && salePrice < regularPrice
    const currentPrice = salePrice || regularPrice
    const styles = useMultiStyleConfig('OrderDetail')
    const productAttributes = [color, size, length].filter(Boolean).join(', ')
    const {locale} = useMultiSite()
    const brand =
        orderBrand === BRAND_NAME || locale?.countryCode === 'GB' ? BRAND_NAME : orderBrand

    return (
        <Flex
            gap={4}
            alignItems="start"
        >
            <Image src={productImageURL} maxW="140px" maxH="206px" w="100%" h="100%" />
            <Stack display="flex" flexDirection="column" gap="12px">
                <Box display="flex" flexDirection="column" gap="8px">
                    <Box display="flex" flexDirection="column" gap="4px">
                        <Text {...styles.brandNameText}>{productName}</Text>
                        {brand === BRAND_NAME && (
                            <Text {...styles.brandNameShippingText}>{brand}</Text>
                        )}
                    </Box>
                    <Flex alignItems="center" gap={2}>
                        <Text {...styles.quantityPriceShippingText}>
                            <FormattedMessage
                                defaultMessage="{quantity} x {unitPrice}"
                                id="account_order_detail.label.quantity_price"
                                values={{
                                    quantity,
                                    unitPrice: (
                                        <Text as="span" color={isOnSale ? 'red.600' : 'black'}>
                                            <FormattedNumber
                                                value={currentPrice}
                                                style="currency"
                                                currency={currency}
                                            />
                                        </Text>
                                    )
                                }}
                            />
                        </Text>
                        {isOnSale && (
                            <Text {...styles.saleOnText}>
                                <FormattedNumber
                                    value={regularPrice}
                                    style="currency"
                                    currency={currency}
                                />
                            </Text>
                        )}
                    </Flex>
                </Box>
                <Box>
                    {productAttributes && (
                        <Text {...styles.productAttributesText}>{productAttributes}</Text>
                    )}
                </Box>
            </Stack>
        </Flex>
    )
}

ProductCard.propTypes = {
    productImageURL: PropTypes.string,
    orderBrand: PropTypes.string,
    productName: PropTypes.string,
    color: PropTypes.string,
    size: PropTypes.string,
    length: PropTypes.string,
    quantity: PropTypes.number,
    regularPrice: PropTypes.number,
    salePrice: PropTypes.number,
    currency: PropTypes.string,
    styleNumber: PropTypes.string
}

const ShippingMethodIcon = ({isPickupInStore, shippingMethodId}) => {
    if (shippingMethodId === STANDARD_ECOM_SHIPPING_ID) {
        return (
            <Flex width="24px" height="24px">
                <ShippingIcon />
            </Flex>
        )
    }

    if (isPickupInStore) {
        return (
            <Flex width="24px" height="24px">
                <InStoreShipping />
            </Flex>
        )
    }

    return null
}

ShippingMethodIcon.propTypes = {
    isPickupInStore: PropTypes.bool,
    shippingMethodId: PropTypes.string
}

export const OrderProducts = ({
    orderProductsData,
    currency,
    mode,
    orderData
}) => {
    const {isOnlineOrder, shippingMethodDetails, bopisStoreNumber, shippingMethod, orderBrand, storeInfo} = orderData
    const {formatMessage} = useIntl()
    const styles = useMultiStyleConfig('OrderDetail')
    // Calculate total quantity - orderProductsData is an array for ITEMS mode, object for PACKAGES mode
    const opdTotalQuantity = Array.isArray(orderProductsData)
        ? orderProductsData.reduce((sum, item) => sum + (item.quantity || 0), 0)
        : 0

    const renderPackages = () => {
        const messages = defineMessages({
            packageNumberItems: {
                id: 'account_order_detail.label.package_number_items',
                defaultMessage:
                    'Package {packageNumber} ({totalItems, plural, one {# Item} other {# Items}})'
            },
            returnedItems: {
                id: 'account_order_detail.label.returned_items',
                defaultMessage:
                    'Returned Items ({totalItems, plural, one {# Item} other {# Items}})'
            },
            notShipped: {
                id: 'account_order_detail.label.not_shipped',
                defaultMessage:
                    'Not Shipped Yet ({totalItems, plural, one {# Item} other {# Items}})'
            }
        })

        return (
            <Flex flexDir="column" gap={6}>
                {orderProductsData?.packages?.map((pkg, index) => {
                    const returnedItems =
                        pkg.items?.filter((item) =>
                            Object.values(ORDER_RETURN_RAW_STATUS).includes(
                                item.statusRaw?.toLowerCase()
                            )
                        ) || []
                    const nonReturnedItems =
                        pkg.items?.filter(
                            (item) =>
                                !Object.values(ORDER_RETURN_RAW_STATUS).includes(
                                    item.statusRaw?.toLowerCase()
                                )
                        ) || []

                    const nonReturnedTotalQuantity = nonReturnedItems.reduce(
                        (sum, item) => sum + (item.quantity || 0),
                        0
                    )
                    const returnedItemsTotalQuantity = returnedItems.reduce(
                        (sum, item) => sum + (item.quantity || 0),
                        0
                    )

                    return (
                        <Flex flexDir="column" gap={4} key={pkg.trackingNumber || index}>
                            {nonReturnedItems.length > 0 && (
                                <>
                                    <Text fontSize="md" fontWeight="bold" textTransform="uppercase">
                                        {formatMessage(messages.packageNumberItems, {
                                            packageNumber: index + 1,
                                            totalItems: nonReturnedTotalQuantity
                                        })}
                                    </Text>
                                    {nonReturnedItems.map((product) => (
                                        <ProductCard
                                            {...product}
                                            currency={currency}
                                            orderBrand={orderBrand}
                                            key={product.sku}
                                        />
                                    ))}
                                </>
                            )}

                            {returnedItems.length > 0 && (
                                <>
                                    <Text fontSize="md" fontWeight="bold" textTransform="uppercase">
                                        {formatMessage(messages.returnedItems, {
                                            totalItems: returnedItemsTotalQuantity
                                        })}
                                    </Text>
                                    {returnedItems.map((product) => (
                                        <ProductCard
                                            {...product}
                                            currency={currency}
                                            orderBrand={orderBrand}
                                            key={product.sku}
                                        />
                                    ))}
                                </>
                            )}
                        </Flex>
                    )
                })}

                {orderProductsData?.nonPackedItems?.length > 0 && (
                    <Flex flexDir="column" gap={4}>
                        {(() => {
                            const returnedNonPacked = orderProductsData.nonPackedItems.filter(
                                (item) =>
                                    Object.values(ORDER_RETURN_RAW_STATUS).includes(
                                        item.statusRaw?.toLowerCase()
                                    )
                            )
                            const nonReturnedNonPacked = orderProductsData.nonPackedItems.filter(
                                (item) =>
                                    !Object.values(ORDER_RETURN_RAW_STATUS).includes(
                                        item.statusRaw?.toLowerCase()
                                    )
                            )

                            const returnedNonPackedTotalQuantity = returnedNonPacked.reduce(
                                (sum, item) => sum + (item.quantity || 0),
                                0
                            )
                            const nonReturnedNonPackedTotalQuantity = nonReturnedNonPacked.reduce(
                                (sum, item) => sum + (item.quantity || 0),
                                0
                            )

                            return (
                                <>
                                    {nonReturnedNonPacked.length > 0 && (
                                        <>
                                            <Text
                                                fontSize="md"
                                                fontWeight="bold"
                                                textTransform="uppercase"
                                            >
                                                {formatMessage(messages.notShipped, {
                                                    totalItems: nonReturnedNonPackedTotalQuantity
                                                })}
                                            </Text>
                                            {nonReturnedNonPacked.map((product) => (
                                                <ProductCard
                                                    {...product}
                                                    currency={currency}
                                                    orderBrand={orderBrand}
                                                    key={product.sku}
                                                />
                                            ))}
                                        </>
                                    )}

                                    {returnedNonPacked.length > 0 && (
                                        <>
                                            <Text
                                                fontSize="md"
                                                fontWeight="bold"
                                                textTransform="uppercase"
                                            >
                                                {formatMessage(messages.returnedItems, {
                                                    totalItems: returnedNonPackedTotalQuantity
                                                })}
                                            </Text>
                                            {returnedNonPacked.map((product) => (
                                                <ProductCard
                                                    {...product}
                                                    currency={currency}
                                                    orderBrand={orderBrand}
                                                    key={product.sku}
                                                />
                                            ))}
                                        </>
                                    )}
                                </>
                            )
                        })()}
                    </Flex>
                )}
            </Flex>
        )
    }

    const renderItems = () => {
        const messages = defineMessages({
            packageItems: {
                id: 'account_order_detail.label.package_items',
                defaultMessage: 'Your Package ({totalItems, plural, one {# Item} other {# Items}})'
            },
            returnedItems: {
                id: 'account_order_detail.label.returned_items',
                defaultMessage:
                    'Returned Items ({totalItems, plural, one {# Item} other {# Items}})'
            },
            shippingMethod: {
                id: 'account_order_detail.label.shipping_method',
                defaultMessage: 'Shipping Method'
            },
            purchasedItems: {
                id: 'account_order_detail.label.purchased_items',
                defaultMessage:
                    'Purchased items'
            }
        })

        const returnedItems =
            orderProductsData?.filter((item) =>
                Object.values(ORDER_RETURN_RAW_STATUS).includes(item.statusRaw?.toLowerCase())
            ) || []
        const isPickupInStore = Boolean(bopisStoreNumber) && !shippingMethod
        const shippingMethodDisplay = isPickupInStore ? SHIPPING_METHODS.PICKUP_IN_STORE.DISPLAY_NAME : shippingMethodDetails?.displayName || shippingMethod || '-'
        const shippingFeeDisplay = isPickupInStore ? 'FREE' : null
        const allReturned = returnedItems.length === orderProductsData?.length
        return (
            <Flex flexDir="column" gap={4} border="1px solid #ECE9E9" padding="16px">
                <Text {...styles.shippingMethodHeading}>
                    {isOnlineOrder ? formatMessage(messages.shippingMethod) : formatMessage(messages.purchasedItems)}
                </Text>
                {isOnlineOrder && (
                    <Stack width="100%" flexDirection="row" gap="8px">
                        <ShippingMethodIcon
                            isPickupInStore={isPickupInStore}
                            shippingMethodId={shippingMethodDetails?.id}
                        />
                        <Flex width="90%" flexDirection="column" marginTop="-4px">
                            <Flex
                                justifyContent="space-between"
                                alignItems="center"
                                textTransform="uppercase"
                                flexDirection="row"
                            >
                                <Text {...styles.shippingMethodText}>
                                    {shippingMethodDisplay}
                                </Text>
                                {isPickupInStore && (
                                    <Text {...styles.shippingMethodNumber}>
                                        {shippingFeeDisplay}
                                    </Text>
                                )}
                            </Flex>

                            {!isPickupInStore && shippingMethodDetails?.id !== E_GIFT_CARD_SHIPPING_ID && (
                                <Flex>
                                    <Text {...styles.shippingMethodbusinessDaysText}>
                                        {shippingMethodDetails?.description}
                                    </Text>
                                </Flex>
                            )}
                            {isPickupInStore && (
                                <Text {...styles.shippingMethodStoreInfo}>
                                    <Flex flexDirection="column">
                                        <Text >{storeInfo?.address?.address1}</Text>
                                        <Text>
                                            {storeInfo?.address?.city}, {storeInfo?.address?.state}, {storeInfo?.address?.zip}
                                        </Text>
                                    </Flex>
                                </Text>
                            )}
                        </Flex>
                    </Stack>
                )}

                {orderProductsData?.map((product) => (
                    <ProductCard
                        {...product}
                        currency={currency}
                        orderBrand={orderBrand}
                        key={product.sku}
                    />
                ))}
            </Flex>
        )
    }

    return (
        <>
            {mode === ORDER_PRODUCTS_RENDER_MODES.PACKAGES ? renderPackages() : renderItems()}
        </>
    )
}

OrderProducts.propTypes = {
    orderProductsData: PropTypes.oneOfType([
        PropTypes.arrayOf(PropTypes.object),
        PropTypes.shape({
            packages: PropTypes.arrayOf(PropTypes.object),
            nonPackedItems: PropTypes.arrayOf(PropTypes.object)
        })
    ]).isRequired,
    orderBrand: PropTypes.string,
    currency: PropTypes.string.isRequired,
    mode: PropTypes.oneOf(Object.values(ORDER_PRODUCTS_RENDER_MODES)).isRequired,
    shippingMethod: PropTypes.string,
    shippingFee: PropTypes.string,
    isOnlineOrder: PropTypes.bool
}
